import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { documentWriteError } from "@/domains/documents/services/pinnedDocuments";
import { queryClient } from "@/lib/queryClient";
import {
  invalidateBuildingStorageQuota,
  storageAccessErrorFrom,
  storageQuotaErrorFrom,
} from "@/domains/documents/services/buildingStorageQuota";
import { eventLogger } from "@/utils/eventLogger";
import type { EventLogEntry } from "@/types/eventTypes";
import { getAllAncestorFolderIds, getAllDescendantFolderIds } from "./queries";
import { profileDisplayName } from "./mappers";

/**
 * Flush every cached document/folder/trash react-query result. Called after
 * any soft-delete or restore so views that don't fetch on mount (5-minute
 * staleTime is set globally) reflect the new state. Building-id agnostic on
 * purpose — folder/doc moves can cross buildings, and the cache is small.
 */
function invalidateDocumentCaches(): void {
  invalidateBuildingStorageQuota();
  queryClient.invalidateQueries({ queryKey: ["trashed-documents"] });
  queryClient.invalidateQueries({ queryKey: ["document-folders"] });
  queryClient.invalidateQueries({ queryKey: ["documents"] });
}

/**
 * Soft delete a document.
 */
export async function deleteDocument(id: string, userId: string) {
  const numericId = Number(id);
  const numericUserId = Number(userId);
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from("documents")
    .update({
      is_deleted: true,
      deleted_at: now,
      deleted_by: numericUserId,
    })
    .eq("id", numericId)
    .select(DOCUMENT_EVENT_COLUMNS);

  if (error) throw documentWriteError(error);

  await logDocumentsDeleted((data ?? []) as unknown as DocumentEventRow[]);

  invalidateDocumentCaches();

  return { success: true } as const;
}

/**
 * Bulk update folder for multiple documents.
 *
 * Returns the ids the server actually wrote, in `movedIds`. RLS *filters* an
 * UPDATE rather than refusing it: rows the caller may not modify are silently
 * dropped from the statement's scope and PostgREST still answers
 * `{ error: null }`, so a resolved call is no evidence that every requested
 * document moved. Reading the ids back with `.select("id")` is the only way a
 * caller can tell a whole move from a partial one and report the difference.
 */
export async function bulkUpdateFolder(
  documentIds: string[],
  folderId: string | null,
  userId: string,
  _projectId?: string,
  _buildingId?: string,
  _orgId?: string
) {
  const numericDocIds = documentIds.map(Number);
  const numericUserId = Number(userId);
  const numericFolderId = folderId ? Number(folderId) : null;
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from("documents")
    .update({
      folder_id: numericFolderId,
      modified_at: now,
      modified_by: numericUserId,
    })
    .in("id", numericDocIds)
    .select("id");

  if (error) throw documentWriteError(error);

  const movedIds = (data ?? []).map((row) => String(row.id));

  return { success: true, updated: movedIds.length, movedIds } as const;
}

/**
 * Bulk update project for multiple documents.
 */
export async function bulkUpdateProject(
  documentIds: string[],
  projectId: string | null,
  userId: string
) {
  const numericDocIds = documentIds.map(Number);
  const numericProjectId = projectId ? Number(projectId) : null;
  const numericUserId = Number(userId);
  const now = new Date().toISOString();

  const { error } = await supabase
    .from("documents")
    .update({
      project_id: numericProjectId,
      modified_at: now,
      modified_by: numericUserId,
    })
    .in("id", numericDocIds);

  if (error) throw documentWriteError(error);
  return { success: true, updated: documentIds.length } as const;
}

/** Row shape returned by the verified UPDATE's `.select()`. */
interface VerifiedRow {
  id: number;
  verified_at: string | null;
  verified_by: number | null;
  verified_by_profile: { first_name: string | null; last_name: string | null } | null;
}

/** What the server decided a document's Verified mark is, after a write. */
export interface VerifiedResult {
  id: number;
  verified_at: string | null;
  verified_by: number | null;
  verified_by_name: string | null;
}

/** The outcome of a verify write, split by what the server actually did. */
export interface VerifiedWriteResult {
  /** Rows this write TRANSITIONED — the only rows an Undo may act on. */
  transitioned: VerifiedResult[];
  /**
   * Rows the write matched nothing for because they were ALREADY in the
   * requested state, read back as the server holds them. Nothing failed for
   * these: applying them is what pulls a stale badge straight, and counting
   * them keeps the click from reading as a refusal.
   */
  alreadyInState: VerifiedResult[];
  /**
   * Rows read back NOT in the requested state: the write was refused for them,
   * or the caller never sent them because the cached mark it filtered on was
   * stale. Neither is settled, so they are counted against the click — and
   * applying them is what corrects the badge that misled it, without which the
   * next click filters on the same stale mark and reports the same failure
   * forever.
   */
  unchanged: VerifiedResult[];
  /**
   * How many ids the write did not transition and the reconcile read could not
   * classify, because that read itself failed. Their state is UNKNOWN — not
   * refused. A caller that counts them as refusals answers a write that did
   * everything asked of it with "only 1 of 2 could be updated", and every
   * retry reproduces it, since the retry re-sends rows that are now in the
   * target state and so transitions nothing either.
   */
  unreconciled: number;
}

/** The verified mark plus its attribution, resolved through the named FK. */
const VERIFIED_COLUMNS =
  "id, verified_at, verified_by, " +
  "verified_by_profile:user_profiles!documents_verified_by_fk(first_name, last_name)";

function toVerifiedResult(row: VerifiedRow): VerifiedResult {
  return {
    id: row.id,
    verified_at: row.verified_at ?? null,
    verified_by: row.verified_by ?? null,
    verified_by_name: profileDisplayName(row.verified_by_profile),
  };
}

/**
 * Set or clear the Verified mark on one or more documents.
 *
 * The values sent here are only a *request*: the DB's
 * `trg_stamp_documents_verified` trigger overwrites `verified_at` with `now()`
 * and `verified_by` with the calling user's profile id (or clears both when
 * unverifying). We therefore read the row back with `.select()` and return what
 * the server actually stored, so the badge shows the DB's attribution rather
 * than a timestamp the client made up.
 *
 * Permission is enforced by the `documents_update` RLS policy (org MODIFY_DATA
 * or project-scoped MODIFY_PROJECT_FILES) — the same gate as rename.
 *
 * A row can go unwritten for two very different reasons, and the caller has to
 * tell them apart: RLS refused it, or it was already in the requested state
 * (a stale cache, or somebody else got there first). So every id the write did
 * not return is READ BACK: the ones already in the requested state come home in
 * `alreadyInState`, which makes the write idempotent again — nothing failed,
 * and the re-read rows are what repairs the cache that sent us here. The rest
 * come home in `unchanged`, still the server's own rows and still worth
 * applying — unless that read failed, in which case the leftovers are counted
 * in `unreconciled` and claimed as neither.
 *
 * `selectionIds` is the whole click, which is wider than `documentIds` whenever
 * the caller held rows back from the write — a bulk verify does not re-send the
 * rows it believes are verified already, because the unverify direction would
 * then clear an attribution the user never touched. Those rows are still the
 * user's question, so they are reconciled like any other untransitioned id: no
 * count the caller reports rests on the client's cached mark, which is the very
 * thing suspected of being stale.
 */
export async function setDocumentsVerified(
  documentIds: string[],
  verified: boolean,
  selectionIds: string[] = documentIds
): Promise<VerifiedWriteResult> {
  const numericDocIds = toNumericIds(documentIds);
  // The write's own ids are part of the selection whatever the caller passed.
  const numericSelectionIds = Array.from(
    new Set([...numericDocIds, ...toNumericIds(selectionIds)])
  );
  if (numericSelectionIds.length === 0) {
    return { transitioned: [], alreadyInState: [], unchanged: [], unreconciled: 0 };
  }

  const transitioned = numericDocIds.length > 0 ? await writeVerified(numericDocIds, verified) : [];

  const written = new Set(transitioned.map((row) => row.id));
  const untouched = numericSelectionIds.filter((id) => !written.has(id));
  if (untouched.length === 0) {
    if (transitioned.length > 0) invalidateDocumentCaches();
    return { transitioned, alreadyInState: [], unchanged: [], unreconciled: 0 };
  }

  // A plain SELECT, so a row the user may see but not modify comes back in its
  // source state and stays out of `alreadyInState` — exactly the refusal the
  // caller must report.
  const { data: current, error: readError } = await supabase
    .from("documents")
    .select(VERIFIED_COLUMNS)
    .in("id", untouched);

  if (readError) {
    // The write itself landed. Throwing here would discard the rows it
    // transitioned and answer a successful write with a hard error, so keep
    // the real outcome — and hand the leftovers back as UNKNOWN rather than as
    // refusals. This read is a reporting refinement, not part of the write's
    // success condition: without it we cannot tell an RLS refusal from a row
    // that was already in the requested state, and guessing "refused" makes a
    // write that changed everything it needed to read as a failure the user
    // can never retry their way out of.
    Sentry.captureException(new Error(readError.message), {
      tags: { area: "documents", operation: "verify_reconcile" },
      // Without the ids and the direction the event cannot be tied back to the
      // rows the user was told nothing definite about.
      extra: { verified, requested: numericSelectionIds.length, untouched },
    });
    if (transitioned.length > 0) invalidateDocumentCaches();
    return { transitioned, alreadyInState: [], unchanged: [], unreconciled: untouched.length };
  }

  const reconciled = ((current ?? []) as unknown as VerifiedRow[]).map(toVerifiedResult);
  const isInState = (row: VerifiedResult) => (row.verified_at != null) === verified;

  // The caller sends exactly the rows its cache says are NOT in the target
  // state and holds back the ones it says are — so a read-back that disagrees
  // with that split is a badge the cache has wrong, and the `["documents"]`
  // entries feeding the other views carry the same wrong row. The flush has to
  // cover it: the reconcile-only path performs no write at all, so leaving it
  // to `writeVerified` left every list still painting the mark this read had
  // just corrected.
  const believedOutOfState = new Set(numericDocIds);
  const corrected = reconciled.some((row) => isInState(row) === believedOutOfState.has(row.id));
  if (transitioned.length > 0 || corrected) invalidateDocumentCaches();

  return {
    transitioned,
    alreadyInState: reconciled.filter(isInState),
    unchanged: reconciled.filter((row) => !isInState(row)),
    unreconciled: 0,
  };
}

const toNumericIds = (ids: string[]) => ids.map(Number).filter((id) => !Number.isNaN(id));

/**
 * The UPDATE itself: returns the rows it TRANSITIONED, and nothing else.
 *
 * Flushing the caches is the caller's, not this function's — a reconcile that
 * corrects a stale badge has to flush them too, and it never gets here.
 */
async function writeVerified(
  numericDocIds: number[],
  verified: boolean
): Promise<VerifiedResult[]> {
  const write = supabase
    .from("documents")
    .update({
      // Any non-null instant asks the trigger to verify; it supplies the real
      // one. `verified_by` is deliberately not sent — the server owns it.
      verified_at: verified ? new Date().toISOString() : null,
    })
    .in("id", numericDocIds);

  // Match only the rows this write TRANSITIONS. The trigger freezes a row that
  // is already in the target state, but the UPDATE still matches it and
  // `.select()` still returns it — unchanged, and indistinguishable from a row
  // we actually wrote. A stale client would then be told it verified a row
  // someone else had verified, and its Undo would clear that person's
  // attribution, which the DB cannot restore. With the direction filter the
  // returned rows ARE the transitioned rows, so a short result set is a real
  // partial write rather than a silent lie.
  const directed = verified ? write.is("verified_at", null) : write.not("verified_at", "is", null);

  const { data, error } = await directed.select(VERIFIED_COLUMNS);

  if (error) throw new Error(error.message);

  // The select string is built at runtime, so supabase-js can't infer the
  // embedded relation's shape — name it explicitly rather than falling back to
  // `any`.
  return ((data ?? []) as unknown as VerifiedRow[]).map(toVerifiedResult);
}

/** The columns a `document.deleted` / `document.restored` event is built from. */
export const DOCUMENT_EVENT_COLUMNS = "id, filename, building_id, org_id";

export interface DocumentEventRow {
  id: number;
  filename: string | null;
  building_id: number | null;
  org_id: string | null;
}

interface DocumentFolderIdRow {
  folder_id: number | null;
}

/**
 * Audit events for the rows a trash or a restore actually moved.
 *
 * Written at the repository layer rather than in a store, because no single
 * caller sees every row an action touches: `folderService.deleteFolder` arrives
 * with no selection and no store context at all, and both the delete and the
 * restore folder cascades (`trg_cascade_soft_delete_folder` /
 * `trg_cascade_restore_folder`) move documents nobody named. Documents →
 * History reads both actions from `event_logs`, so an unlogged path is a
 * permanent hole in the timeline. The org id comes off the row for the same
 * reason — the caller may not hold one.
 *
 * The whole batch goes in one `eventLogger.logMany` — a folder trash can move
 * hundreds of documents, and a request per row would both saturate the browser's
 * connection pool and lose whatever was still in flight when the user navigated
 * away. Awaited by every caller for that second reason: nothing retries this.
 */
function logDocumentEvents(
  eventType: "document.deleted" | "document.restored",
  rows: DocumentEventRow[]
): Promise<void> {
  // Same document reached by both halves of one action (a selected row inside a
  // selected folder) must not render two entries.
  const byId = new Map(rows.map((row) => [row.id, row]));
  const entries: EventLogEntry[] = [];
  const withoutOrg: number[] = [];

  byId.forEach((row) => {
    if (!row.org_id) {
      withoutOrg.push(row.id);
      return;
    }
    entries.push({
      event_type: eventType,
      entity_type: "document",
      entity_id: String(row.id),
      org_id: row.org_id,
      metadata: { filename: row.filename ?? "unknown", buildingId: row.building_id },
    });
  });

  if (withoutOrg.length > 0) {
    // `documents.org_id` is nullable, and `event_logs` needs one. Silently
    // dropping these leaves the document moved with nothing on the timeline to
    // say so — report it, the way every other failure on this path is reported,
    // or the hole is undiagnosable in production.
    const message = `Skipped ${eventType} audit events: documents with no org_id`;
    // Literal first argument: a built format string here is what
    // `unsafe-formatstring` flags.
    console.error("Skipped audit events: documents with no org_id", { eventType, withoutOrg });
    Sentry.captureMessage(message, {
      level: "error",
      tags: { feature: "document-history", stage: "audit-event-org-id" },
      extra: { eventType, documentIds: withoutOrg },
    });
  }

  return eventLogger.logMany(entries);
}

export function logDocumentsDeleted(rows: DocumentEventRow[]): Promise<void> {
  return logDocumentEvents("document.deleted", rows);
}

export function logDocumentsRestored(rows: DocumentEventRow[]): Promise<void> {
  return logDocumentEvents("document.restored", rows);
}

/**
 * Rows per page of an audit read. PostgREST truncates any unbounded response at
 * `max_rows` (1000 here, as `useTemplateFormDocuments` documents) with no error
 * and no way to tell a complete result from a clipped one — so every read that
 * feeds an audit event pages instead, and a page that comes back full asks for
 * the next one.
 */
const AUDIT_ROW_PAGE_SIZE = 1000;

/**
 * Ids per `in.(…)` filter. PostgREST reads filters off the request URL, so the
 * list has to stay well inside the gateway's URL limit — a thousand numeric ids
 * is roughly 8KB (see `SCOPED_DOCUMENT_INDEX_LIMIT`).
 */
const AUDIT_ID_FILTER_SIZE = 500;

interface PostgrestPage<T> {
  data: T[] | null;
  error: { message: string } | null;
}

/**
 * Every row matching an `in.(…)` filter, whatever the size of either side.
 *
 * The ids are chunked so the filter fits in the URL, and each chunk is paged so
 * a match set larger than `max_rows` is not silently clipped. Trashing a folder
 * subtree holding more than a thousand visible documents used to log a
 * `document.deleted` event for an arbitrary, unordered thousand of them: the
 * rest sat in the Trash with nothing in `event_logs`, so history claimed they
 * were never deleted, and a later restore logged a `document.restored` with no
 * matching delete above it.
 */
async function fetchAllByIdFilter<T>(
  ids: number[],
  page: (chunk: number[], from: number, to: number) => PromiseLike<PostgrestPage<T>>
): Promise<T[]> {
  const rows: T[] = [];
  for (let start = 0; start < ids.length; start += AUDIT_ID_FILTER_SIZE) {
    const chunk = ids.slice(start, start + AUDIT_ID_FILTER_SIZE);
    for (let offset = 0; ; offset += AUDIT_ROW_PAGE_SIZE) {
      const { data, error } = await page(chunk, offset, offset + AUDIT_ROW_PAGE_SIZE - 1);
      if (error) throw new Error(error.message);
      const batch = data ?? [];
      rows.push(...batch);
      if (batch.length < AUDIT_ROW_PAGE_SIZE) break;
    }
  }
  return rows;
}

/**
 * Read the rows an event is built from, for ids a mutation has already moved.
 *
 * `visibleOnly` narrows to what a document list actually shows — live, one row
 * per lineage, no generated DWG child PDFs. Use it for rows the user never
 * picked by hand (a version-group expansion, a folder cascade): without it a
 * re-uploaded file logs an event per superseded version and the history renders
 * N rows for a file that only ever showed one.
 *
 * Never fails the caller: a missing audit event is worth less than the
 * delete/restore the user asked for.
 */
export async function documentEventRows(
  ids: number[],
  { visibleOnly = false }: { visibleOnly?: boolean } = {}
): Promise<DocumentEventRow[]> {
  if (ids.length === 0) return [];
  try {
    return await fetchAllByIdFilter<DocumentEventRow>(ids, (chunk, from, to) => {
      let query = supabase.from("documents").select(DOCUMENT_EVENT_COLUMNS).in("id", chunk);
      if (visibleOnly) {
        query = query
          .eq("is_deleted", false)
          .eq("hidden", false)
          .eq("is_latest_version", true)
          .or("is_dwg_child.is.null,is_dwg_child.eq.false");
      }
      // Ordered so the pages address a stable result set.
      return query.order("id", { ascending: true }).range(from, to) as unknown as PromiseLike<
        PostgrestPage<DocumentEventRow>
      >;
    });
  } catch (e) {
    console.error("Failed to read documents for audit logging", e);
    Sentry.captureException(e);
    return [];
  }
}

/**
 * The documents a folder trash is about to cascade over.
 *
 * Read BEFORE the folder rows are updated: the trigger flips the documents in
 * the same transaction, after which nothing distinguishes them from documents
 * trashed an hour ago. Narrowed the way the history timeline reads documents —
 * one row per lineage, no hidden rows, no generated DWG child PDFs — so the
 * cascade logs one event per row a user can actually see, matching what the
 * individual delete logs. `is_latest_version` is accurate here for the same
 * reason the read has to happen first: the rows are still live, so the flag
 * still describes the lineage as the lists show it.
 *
 * Never fails the trash: a missing audit event is worth less than the delete the
 * user asked for, so an error here is reported and the caller carries on.
 */
async function documentsCascadedByFolders(folderIds: number[]): Promise<DocumentEventRow[]> {
  try {
    const subtreeIds = await getAllDescendantFolderIds(folderIds);
    return await fetchAllByIdFilter<DocumentEventRow>(
      subtreeIds,
      (chunk, from, to) =>
        supabase
          .from("documents")
          .select(DOCUMENT_EVENT_COLUMNS)
          .in("folder_id", chunk)
          .eq("is_deleted", false)
          .eq("hidden", false)
          .eq("is_latest_version", true)
          .or("is_dwg_child.is.null,is_dwg_child.eq.false")
          .order("id", { ascending: true })
          .range(from, to) as unknown as PromiseLike<PostgrestPage<DocumentEventRow>>
    );
  } catch (e) {
    console.error("Failed to read folder contents for delete logging", e);
    Sentry.captureException(e);
    return [];
  }
}

/**
 * Bulk soft delete multiple documents (move to trash).
 * When folderIds are provided, soft-deletes the folder rows themselves;
 * the trg_cascade_soft_delete_folder Postgres trigger then recursively
 * soft-deletes child folders and the documents inside them as part of
 * the same transaction. Folder rows are kept (not hard-deleted) so that
 * restoring a folder can re-attach its previously orphaned documents.
 */
export async function bulkMoveToTrash(documentIds: string[], userId: string, folderIds?: string[]) {
  const numericDocIds = documentIds.map(Number);
  const numericUserId = Number(userId);
  const now = new Date().toISOString();

  const cascaded = folderIds?.length ? await documentsCascadedByFolders(folderIds.map(Number)) : [];

  let trashed: DocumentEventRow[] = [];
  if (numericDocIds.length > 0) {
    // The update returns the rows it changed, so the event metadata is the
    // server's own filename/building rather than whatever the caller happened
    // to be holding when it decided to delete.
    const { data, error } = await supabase
      .from("documents")
      .update({
        is_deleted: true,
        deleted_at: now,
        deleted_by: numericUserId,
      })
      .in("id", numericDocIds)
      .select(DOCUMENT_EVENT_COLUMNS);

    if (error) throw documentWriteError(error);
    trashed = (data ?? []) as unknown as DocumentEventRow[];
  }

  if (folderIds?.length) {
    const numericFolderIds = folderIds.map(Number);
    const { error: folderError } = await supabase
      .from("document_folders")
      .update({
        is_deleted: true,
        deleted_at: now,
        deleted_by: numericUserId,
      })
      .in("id", numericFolderIds);
    if (folderError) {
      // The documents UPDATE above has already committed — its rows are in the
      // Trash whatever happens to the folders. Flush their events before
      // rethrowing or they are lost for good: nothing retries this call, and
      // history would show those documents as never deleted. The cascade rows
      // are deliberately NOT logged; that half never ran.
      await logDocumentsDeleted(trashed);
      throw documentWriteError(folderError);
    }
  }

  await logDocumentsDeleted([...trashed, ...cascaded]);

  invalidateDocumentCaches();

  return { success: true, count: numericDocIds.length } as const;
}

/**
 * Bulk restore multiple documents from trash. Also un-deletes every folder
 * ancestor in each restored document's path so the folder tree returns to
 * its pre-delete shape.
 */
export async function bulkRestore(documentIds: string[]) {
  const numericDocIds = documentIds.map(Number);
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from("documents")
    .update({
      is_deleted: false,
      deleted_at: null,
      deleted_by: null,
      modified_at: now,
    })
    .in("id", numericDocIds)
    .select(DOCUMENT_EVENT_COLUMNS);

  if (error) throw new Error(error.message);

  // Paired with the delete side: every row this un-deleted gets its
  // `document.restored` event here, not in the store, so a restore issued from
  // anywhere lands on the timeline. These ids were picked row by row in Trash,
  // so they are logged as given — the narrowing belongs to the cascade below,
  // which sweeps up rows nobody named.
  await logDocumentsRestored((data ?? []) as unknown as DocumentEventRow[]);

  await restoreFolderAncestorsForDocs(numericDocIds);

  invalidateDocumentCaches();

  return { success: true, count: documentIds.length } as const;
}

/**
 * The trashed documents an ancestor restore may cascade over, by id.
 *
 * `trg_cascade_restore_folder` un-deletes the documents of every folder it
 * restores (and descends into child folders), so a folder round-trip brings
 * back rows no caller ever lists — the mirror of the delete cascade. Which of
 * these the trigger actually takes is decided by its own deleted_at window, so
 * this is a candidate set: the caller re-reads it afterwards and logs only what
 * came back live. Only trashed ancestors can cascade at all — the trigger fires
 * on a true→false flip — so a restore into folders that were never trashed
 * costs nothing.
 *
 * Never fails the restore, for the same reason the delete-side read doesn't.
 */
async function documentsCascadedByFolderRestore(ancestorIds: number[]): Promise<number[]> {
  try {
    const trashedFolders = await fetchAllByIdFilter<{ id: number }>(
      ancestorIds,
      (chunk, from, to) =>
        supabase
          .from("document_folders")
          .select("id")
          .in("id", chunk)
          .eq("is_deleted", true)
          .order("id", { ascending: true })
          .range(from, to) as unknown as PromiseLike<PostgrestPage<{ id: number }>>
    );

    const trashedIds = trashedFolders.map((row) => row.id);
    if (trashedIds.length === 0) return [];

    const subtreeIds = await getAllDescendantFolderIds(trashedIds);
    const rows = await fetchAllByIdFilter<{ id: number }>(
      subtreeIds,
      (chunk, from, to) =>
        supabase
          .from("documents")
          .select("id")
          .in("folder_id", chunk)
          .eq("is_deleted", true)
          .order("id", { ascending: true })
          .range(from, to) as unknown as PromiseLike<PostgrestPage<{ id: number }>>
    );
    return rows.map((row) => row.id);
  } catch (e) {
    console.error("Failed to read folder contents for restore logging", e);
    Sentry.captureException(e);
    return [];
  }
}

/**
 * Walk up the folder tree from each given document's folder_id and clear the
 * soft-delete flags on every ancestor folder. Used by both bulkRestore and
 * the version-group restore in documentService.
 */
export async function restoreFolderAncestorsForDocs(documentIds: number[]): Promise<void> {
  if (documentIds.length === 0) return;

  const { data: docRows, error: docErr } = await supabase
    .from("documents")
    .select("folder_id")
    .in("id", documentIds);
  if (docErr) throw new Error(docErr.message);

  const folderRows = (docRows ?? []) as unknown as DocumentFolderIdRow[];
  const folderIds = Array.from(
    new Set(folderRows.map((row) => row.folder_id).filter((id): id is number => id !== null))
  );
  if (folderIds.length === 0) return;

  const ancestorIds = await getAllAncestorFolderIds(folderIds);
  if (ancestorIds.length === 0) return;

  const cascadeCandidateIds = await documentsCascadedByFolderRestore(ancestorIds);

  const { error: folderErr } = await supabase
    .from("document_folders")
    .update({ is_deleted: false, deleted_at: null, deleted_by: null })
    .in("id", ancestorIds);
  if (folderErr) throw new Error(folderErr.message);

  // Read back rather than assume: the trigger restores only the documents whose
  // deleted_at sits inside its window, so this is the one honest answer to what
  // the cascade took. Without these events a folder trash/restore round-trip
  // leaves history full of "Moved to Trash" rows with no "Restored from Trash"
  // to match — claiming documents are still in Trash when they are not.
  await logDocumentsRestored(await documentEventRows(cascadeCandidateIds, { visibleOnly: true }));
}

/**
 * Bulk permanently delete multiple documents.
 * WARNING: This is a hard delete and cannot be undone.
 */
export async function bulkPermanentDelete(documentIds: string[]) {
  const numericDocIds = documentIds.map(Number);

  const { error } = await supabase.from("documents").delete().in("id", numericDocIds);

  if (error) throw new Error(error.message);
  return { success: true, count: documentIds.length } as const;
}

/**
 * @deprecated Use bulkMoveToTrash instead
 */
export async function bulkDelete(documentIds: string[], userId: string) {
  return bulkMoveToTrash(documentIds, userId);
}

/**
 * Bulk update construction categories for multiple documents.
 */
export async function bulkUpdateCategories(
  documentIds: string[],
  categories: string[],
  userId: string
) {
  const numericDocIds = documentIds.map(Number);
  const numericUserId = Number(userId);
  const now = new Date().toISOString();

  const { error } = await supabase
    .from("documents")
    .update({
      construction_categories: categories,
      modified_at: now,
      modified_by: numericUserId,
    })
    .in("id", numericDocIds);

  if (error) throw new Error(error.message);
  return { success: true, updated: documentIds.length } as const;
}

/**
 * Copy documents to another project.
 * Uses the copy_document RPC to preserve document state and tag associations.
 * Handles filename conflicts by appending numbers (e.g., "file (2).pdf").
 *
 * Returns detailed results including failed document IDs for proper error handling.
 */
export async function copyDocumentsToProject(
  documentIds: string[],
  targetProjectId: string,
  targetBuildingId: string,
  orgId: string,
  userId: string,
  targetFolderId?: string | null,
  _sourceProjectId?: string | null
) {
  if (documentIds.length === 0) {
    return {
      success: true,
      copied: 0,
      failed: 0,
      failedIds: [] as string[],
      attempted: 0,
      quotaFailed: 0,
      quotaFailureMessage: null as string | null,
      permissionDenied: 0,
    } as const;
  }

  let copiedCount = 0;
  const failedIds: string[] = [];
  let quotaFailed = 0;
  let quotaFailureMessage: string | null = null;
  let permissionDenied = 0;

  for (const docId of documentIds) {
    const { data, error } = await supabase.rpc("copy_document", {
      p_source_document_id: Number(docId),
      p_target_project_id: Number(targetProjectId),
      p_target_folder_id: targetFolderId ? Number(targetFolderId) : null,
      p_target_building_id: Number(targetBuildingId),
      p_org_id: orgId,
      p_created_by: Number(userId),
      p_new_filename: null, // Auto-generate unique name
    });

    if (error) {
      const quotaError = storageQuotaErrorFrom(error);
      if (quotaError) {
        quotaFailed += 1;
        quotaFailureMessage ??= quotaError.message;
        failedIds.push(docId);
        continue;
      }
      if (storageAccessErrorFrom(error)) {
        permissionDenied += 1;
        failedIds.push(docId);
        continue;
      }
      const context = {
        docId,
        targetProjectId,
        targetBuildingId,
        targetFolderId: targetFolderId ?? null,
        orgId,
        userId,
      };
      Sentry.captureException(
        error instanceof Error ? error : new Error(String(error?.message ?? error)),
        {
          tags: { feature: "document-copy", stage: "copy_document-rpc" },
          extra: context,
        }
      );
      failedIds.push(docId);
      continue;
    }

    if (
      data &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      "document_id" in data &&
      typeof data.document_id === "number" &&
      Number.isSafeInteger(data.document_id) &&
      data.document_id > 0
    ) {
      copiedCount++;
    } else {
      const context = {
        docId,
        targetProjectId,
        targetBuildingId,
        targetFolderId: targetFolderId ?? null,
        orgId,
        userId,
        invalidRpcResult: true,
      };
      Sentry.captureMessage("copy_document RPC returned an invalid result", {
        level: "error",
        tags: { feature: "document-copy", stage: "copy_document-invalid-result" },
        extra: context,
      });
      failedIds.push(docId);
    }
  }

  const attempted = documentIds.length;
  const failed = failedIds.length;
  // success is true only if all documents were copied
  const success = failed === 0;

  if (copiedCount > 0 || quotaFailed > 0) invalidateBuildingStorageQuota(targetBuildingId);

  return {
    success,
    copied: copiedCount,
    failed,
    failedIds,
    attempted,
    quotaFailed,
    quotaFailureMessage,
    permissionDenied,
  } as const;
}

/**
 * Move documents to another project.
 */
export async function moveDocumentsToProject(
  documentIds: string[],
  _sourceProjectId: string,
  targetProjectId: string,
  _targetBuildingId: string,
  _orgId: string,
  userId: string,
  targetFolderId?: string | null
) {
  if (documentIds.length === 0) return { success: true, moved: 0 } as const;

  const numericDocIds = documentIds.map(Number);
  const numericTargetProjectId = Number(targetProjectId);
  const numericUserId = Number(userId);
  const numericFolderId = targetFolderId ? Number(targetFolderId) : null;
  const now = new Date().toISOString();

  const { error } = await supabase
    .from("documents")
    .update({
      project_id: numericTargetProjectId,
      folder_id: numericFolderId,
      modified_at: now,
      modified_by: numericUserId,
    })
    .in("id", numericDocIds);

  if (error) throw documentWriteError(error);

  return { success: true, moved: documentIds.length } as const;
}
